import { TranslateModule } from '@ngx-translate/core';
// Services
import { LogService } from '@services/util/log.service';
import { BadgeModule } from 'primeng/badge';
import { TabsModule } from 'primeng/tabs';
import { catchError, finalize } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
// Lazy-loaded components
import { ArrivalsTableComponent } from './components/arrivals-table/arrivals-table.component';
import { BuildingFilterComponent } from './components/building-filter/building-filter.component';
import { DeparturesTableComponent } from './components/departures-table/departures-table.component';
import { ArrivalRow, DepartureRow } from './models/arrival-departure.model';
import { ArrivalDepartureService } from './services/arrival-departure.service';

@Component({
  selector: 'app-arrival-departure',
  standalone: true,
  imports: [CommonModule, TabsModule, TranslateModule, ArrivalsTableComponent, DeparturesTableComponent, BuildingFilterComponent, BadgeModule],
  template: `
    <div class="compact-card">
      <!-- Header -->
      <div class="card-header mb-6">
        <h1 class="text-2xl font-bold">{{ 'arrivalDeparture.pageTitle' | translate }}</h1>
        <p class="mt-1 text-base text-gray-500">{{ 'arrivalDeparture.pageDescription' | translate }}</p>
      </div>

      @if (loading) {
        <div class="flex items-center justify-center py-8">
          <i class="pi pi-spin pi-spinner text-2xl text-primary"></i>
          <span class="ml-3 text-lg">{{ 'common.loading' | translate }}</span>
        </div>
      } @else {
        <!-- Filters -->
        <div class="mx-4 mb-4 flex flex-wrap items-center justify-between gap-4 sm:mx-0">
          <div class="flex-grow">
            <app-building-filter (buildingSelected)="onBuildingSelected($event)"></app-building-filter>
          </div>
        </div>

        <p-tabs
          [value]="activeTabIndex"
          (valueChange)="handleTabChange($event)"
        >
          <p-tablist>
            <p-tab [value]="0">
              <div class="align-items-center flex gap-2">
                <span>{{ 'arrivalDeparture.arrivals' | translate }}</span>
                @if (filteredArrivals.length > 0) {
                  <p-badge
                    [value]="filteredArrivals.length"
                    severity="info"
                  ></p-badge>
                }
              </div>
            </p-tab>
            <p-tab [value]="1">
              <div class="align-items-center flex gap-2">
                <span>{{ 'arrivalDeparture.departures' | translate }}</span>
                @if (filteredDepartures.length > 0) {
                  <p-badge
                    [value]="filteredDepartures.length"
                    severity="info"
                  ></p-badge>
                }
              </div>
            </p-tab>
          </p-tablist>
          <p-tabpanels>
            <p-tabpanel [value]="0">
              <app-arrivals-table [arrivals]="filteredArrivals"></app-arrivals-table>
            </p-tabpanel>
            <p-tabpanel [value]="1">
              <app-departures-table [departures]="filteredDepartures"></app-departures-table>
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
      }
    </div>
  `
})
export class ArrivalDepartureComponent implements OnInit {
  arrivals: ArrivalRow[] = [];
  departures: DepartureRow[] = [];
  filteredArrivals: ArrivalRow[] = [];
  filteredDepartures: DepartureRow[] = [];
  selectedBuilding: string | null = null;
  loading: boolean = false;
  activeTabIndex: number = 0;

  constructor(
    private arrivalDepartureService: ArrivalDepartureService,
    private log: LogService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.loadArrivals();
    this.loadDepartures();
  }

  private loadArrivals(): void {
    this.arrivalDepartureService
      .getArrivals()
      .pipe(
        catchError((error) => {
          this.log.error('Failed to load arrivals', error);
          this.arrivals = [];
          return [];
        })
      )
      .subscribe((data) => {
        this.arrivals = data;
        this.filteredArrivals = [...data];
        this.log.debug('Arrivals loaded', this.arrivals.length);
        this.checkLoadingComplete();
      });
  }

  private loadDepartures(): void {
    this.arrivalDepartureService
      .getDepartures()
      .pipe(
        catchError((error) => {
          this.log.error('Failed to load departures', error);
          this.departures = [];
          return [];
        })
      )
      .subscribe((data) => {
        this.departures = data;
        this.filteredDepartures = [...data];
        this.log.debug('Departures loaded', this.departures.length);
        this.checkLoadingComplete();
      });
  }

  /**
   * Handle building selection from the filter component
   */
  onBuildingSelected(building: string | null): void {
    this.selectedBuilding = building;
    this.applyBuildingFilter();
  }

  /**
   * Apply building filter to both arrivals and departures
   */
  private applyBuildingFilter(): void {
    if (this.selectedBuilding === null) {
      // Show all items if 'All' is selected
      this.filteredArrivals = [...this.arrivals];
      this.filteredDepartures = [...this.departures];
    } else {
      // Filter items by building name
      this.filteredArrivals = this.arrivals.filter((item) => item.flatInfo.buildingName === this.selectedBuilding);
      this.filteredDepartures = this.departures.filter((item) => item.flatInfo.buildingName === this.selectedBuilding);
    }
  }

  /**
   * Check if both arrivals and departures have been loaded
   * and update loading state accordingly
   */
  private checkLoadingComplete(): void {
    // We know both requests completed when this function has been called twice
    // We use the loading flag to track if we're still waiting for data
    if (this.arrivals !== undefined && this.departures !== undefined) {
      this.loading = false;
    }
  }

  /**
   * Handles the tab change event ensuring proper type conversion
   * @param value - The new tab value (could be number or string)
   */
  handleTabChange(value: string | number): void {
    this.activeTabIndex = typeof value === 'string' ? parseInt(value, 10) : value;
  }
}
